import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { logVisitorActivity } from '../../utils/visitorTracking';
import axios from 'axios';
import { Menu, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import PreferencesPanel from '../../components/PreferencesPanel';
import AudioPlayer from '../../components/AudioPlayer';
import FontSizeControl from '../../components/FontSizeControl';
import NewAudioPlayer from '../../components/NewAudioPlayer';

const DX_LOGO_URL = 'https://cdn.art-mate.net/uploads/artmate/202502/thumbnail/20250228_160227_1YqJgAgCUS_p_300_0.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZoneDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, fontSize, t } = useLanguage();
  const [zone, setZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  
  // Track which navigation key we've logged for to prevent duplicates
  const lastLoggedKey = useRef(null);

  // Fetch zone data
  const fetchZone = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/zones/${id}`);
      setZone(response.data);
      console.log('Zone data:', response.data); // Debug log
      
      // Track this page visit only once per navigation
      const currentKey = `${id}-${location.key}`;
      if (lastLoggedKey.current !== currentKey) {
        lastLoggedKey.current = currentKey;
        const zoneData = response.data;
        logVisitorActivity('zone', zoneData.id, zoneData.code, zoneData.title_tc || zoneData.title_en);
      }
    } catch (error) {
      toast.error('Zone not found or not available');
      navigate('/qr-instructions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!language || !['en', 'tc', 'sc'].includes(language)) {
      localStorage.setItem('redirectPath', location.pathname + location.search + location.hash);
      navigate('/');
    }
  }, [location, navigate]);

  // Fetch zone when id changes OR when navigating back/forward
  useEffect(() => {
    fetchZone();
  }, [id, location.key]);

  const getLocalizedText = (field) => {
    if (!zone) return '';

    if (language === 'tc') {
      return zone[`${field}_tc`] || zone[`${field}_en`] || '';
    } else if (language === 'sc') {
      return zone[`${field}_sc`] || zone[`${field}_en`] || '';
    } else {
      return zone[`${field}_en`] || zone[`${field}_tc`] || '';
    }
  };

  const getFontSizeClass = () => {
    if (fontSize === 'small') return 'text-base';
    if (fontSize === 'large') return 'text-2xl';
    return 'text-lg';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black" data-testid="loading-state">
        <div className="animate-pulse text-white/60">Loading...</div>
      </div>
    );
  }

  if (!zone) return null;

  const title = getLocalizedText('title');
  const description = getLocalizedText('description');
  const exhibits = zone.exhibits || [];

  return (
    <div className="min-h-screen bg-black pb-28" data-testid="zone-detail-page">
      {/* Header */}
      <div className="sticky top-0 bg-black/90 backdrop-blur border-b border-white/10 z-10">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between relative">
          <img 
            src={DX_LOGO_URL} 
            alt="DX Logo" 
            className="w-28 object-contain"
          />
          {/* <h1 className="absolute left-1/2 -translate-x-1/2 text-2xl font-heading font-bold text-white">
            {t({ en: 'Exhibition Zone', tc: '展區', sc: '展区' })}
          </h1> */}
          <button
            onClick={() => setPreferencesOpen(true)}
            className="text-white hover:bg-white/10 rounded-lg p-2"
            data-testid="open-preferences"
          >
            <Menu className="w-8 h-8" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Zone Image */}
      {zone.image_url && (
        <div className="relative w-full h-[40vh] bg-white/5">
          <img
            src={`${API}${zone.image_url}?t=${zone.updated_at ? new Date(zone.updated_at).getTime() : Date.now()}`}
            alt={title}
            className="w-full h-full object-cover"
            data-testid="zone-image"
          />

          {/* Overlay back button: circular, half-transparent */}
          <button
            onClick={() => {
              const previousUrl = document.referrer;
              const isSameSite = previousUrl && previousUrl.startsWith(window.location.origin);

              if (isSameSite) {
                const previousPath = new URL(previousUrl).pathname;
                if (previousPath !== '/') {
                  navigate(-1);
                  return;
                }
              }

              navigate('/qr-instructions');
            }}
            className="absolute top-4 left-4 z-5 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/50 hover:bg-white/60 backdrop-blur-sm"
            aria-label={t({ en: 'Back', tc: '返回', sc: '返回' })}
            data-testid="image-back-button"
          >
            <ArrowLeft className="w-5 h-5 text-black" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Zone Content - Double margin */}
      <div className="px-12 pb-20 py-6 max-w-4xl mx-auto space-y-6">
        {/* Title */}
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-white" data-testid="zone-title">
          {title}
        </h2>

        {/* Audio Player and Font Size Control - Same row */}
        <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4">
          {/* <div className="flex-1">
            <NewAudioPlayer audioUrl={`${getLocalizedText('voice_url')}`}/>
          </div> */}
          {/* <div className="flex-shrink-0 border-l border-white/20 pl-4"> */}
          <div className="flex-1">
            <FontSizeControl />
          </div>
        </div>

        {/* Description */}
        <div className="prose prose-lg max-w-none" data-testid="zone-description">
          <p className={`${getFontSizeClass()} text-justify leading-relaxed text-white/90 whitespace-pre-wrap`}>
            {description}
          </p>
        </div>

        {/* Exhibits Section */}
        {exhibits.length > 0 && (
          <div className="pt-6">
            <h3 className="text-2xl font-heading font-bold mb-4 text-white">
              {t({ en: 'Exhibits in this Zone', tc: '本展區展品', sc: '本展区展品' })}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {exhibits.map((exhibit) => (
                <button
                  key={exhibit.id}
                  onClick={() => navigate(`/exhibit/${exhibit.id}`, { state: { from: location.pathname } })}
                  className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-all text-left"
                  data-testid={`exhibit-${exhibit.id}`}
                >
                  {exhibit.image_url ? (
                    <div className="aspect-square bg-white/10">
                      <img
                        src={`${API}${exhibit.image_url}?t=${exhibit.updated_at ? new Date(exhibit.updated_at).getTime() : Date.now()}`}
                        alt={exhibit[`title_${language}`] || exhibit.title_tc}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-square bg-white/10 flex items-center justify-center">
                      <span className="text-4xl font-mono text-white font-bold">{exhibit.code}</span>
                    </div>
                  )}
                  <div className="p-3">
                    <p className="font-medium text-sm line-clamp-2 text-white">
                      {exhibit[`title_${language}`] || exhibit.title_tc}
                    </p>
                    <p className="text-xs text-white/60 mt-1">
                      {t({ en: 'Code:', tc: '代碼：', sc: '代码：' })} {exhibit.code}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation - Done button with larger font */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-white/10 p-4">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-x-4 gap-y-3">
          {/* {zone.survey_url && (<Button
            onClick={() => window.open(zone.survey_url, '_blank')}
            className="w-7/12 h-16 text-2xl font-bold bg-red-300 text-black hover:bg-red-300/90"
            data-testid="done-button"
          >
            {t({ en: 'Survey', tc: '問卷調查', sc: '问卷调查' })}
          </Button>)}
          <Button
            onClick={() => {
              const previousUrl = document.referrer;
              const isSameSite = previousUrl && previousUrl.startsWith(window.location.origin);

              if (isSameSite) {
                const previousPath = new URL(previousUrl).pathname;
                if (previousPath !== '/') {
                  navigate(-1); // Go back to the previous page
                  return;
                }
              }

              // Fallback if not same site, or if previous page was the root
              navigate('/qr-instructions');
            }}
            className={`${zone.survey_url ? 'w-5/12' : 'w-full'} h-16 text-2xl font-bold bg-white text-black hover:bg-white/90`}
            data-testid="done-button"
          >
            {t({ en: 'Back', tc: '返回', sc: '返回' })}
          </Button> */}
          <div className="flex-1">
            <NewAudioPlayer audioUrl={`${getLocalizedText('voice_url')}`}/>
          </div>
          <div className="w-full md:w-auto">
          <Button
            onClick={() => {
              const previousUrl = document.referrer;
              const isSameSite = previousUrl && previousUrl.startsWith(window.location.origin);

              if (isSameSite) {
                const previousPath = new URL(previousUrl).pathname;
                if (previousPath !== '/') {
                  navigate(-1); // Go back to the previous page
                  return;
                }
              }

              // Fallback if not same site, or if previous page was the root
              navigate('/qr-instructions');
            }}
            className={`w-full h-16 text-2xl font-bold bg-white text-black hover:bg-white/90`}
            data-testid="done-button"
          >
            {t({ en: 'Back', tc: '返回', sc: '返回' })}
          </Button>
          </div>
        </div>
      </div>

      {/* Preferences Panel */}
      <PreferencesPanel 
        isOpen={preferencesOpen} 
        onClose={() => setPreferencesOpen(false)} 
      />
    </div>
  );
};

export default ZoneDetail;
